import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  OneDriveBrowse,
  OneDriveImport as OneDriveImportRow,
  OneDriveImportItemIn,
  OneDriveImportList,
  OneDriveItem,
  OneDriveStatus,
} from "../lib/api";

interface Props {
  caseId: number;
  /** Called after files are queued or as imports complete, so the parent can
   *  refresh the Documents/Videos lists. */
  onImported: () => void;
}

interface Crumb {
  id: string | null;
  name: string;
}

function fmtBytes(n?: number | null): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const ACTIVE_POLL_MS = 2000;

export default function OneDriveImport({ caseId, onImported }: Props) {
  const [status, setStatus] = useState<OneDriveStatus | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState<OneDriveImportRow[]>([]);

  // Stable ref so the poller can call the latest onImported without re-arming.
  const onImportedRef = useRef(onImported);
  useEffect(() => { onImportedRef.current = onImported; }, [onImported]);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await api.get<OneDriveStatus>("/integrations/onedrive/status"));
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => { void refreshStatus(); }, [refreshStatus]);

  // ---- active-import progress polling ----
  const pollActive = useCallback(async () => {
    try {
      const resp = await api.get<OneDriveImportList>(
        `/cases/${caseId}/imports/onedrive?active_only=true`,
      );
      setActive((prev) => {
        // Whenever the active set shrinks, something handed off to ingest —
        // refresh the parent so the new doc/video appears in its tab.
        if (resp.items.length !== prev.length) onImportedRef.current();
        return resp.items;
      });
    } catch {
      /* transient; keep last known */
    }
  }, [caseId]);

  useEffect(() => {
    if (active.length === 0) return;
    const t = setInterval(() => { void pollActive(); }, ACTIVE_POLL_MS);
    return () => clearInterval(t);
  }, [active.length, pollActive]);

  // ---- OAuth popup ----
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (ev.origin !== window.location.origin) return;
      const data = ev.data as { source?: string; result?: string } | null;
      if (data?.source === "onedrive-oauth") {
        void refreshStatus();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refreshStatus]);

  const connect = useCallback(async () => {
    try {
      const { auth_url } = await api.get<{ auth_url: string }>(
        "/integrations/onedrive/auth-url",
      );
      const w = 520;
      const h = 640;
      const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
      window.open(
        auth_url,
        "onedrive-oauth",
        `width=${w},height=${h},left=${left},top=${top}`,
      );
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Could not start OneDrive sign-in");
    }
  }, []);

  if (!status || !status.configured) return null;

  return (
    <>
      <div className="onedrive-bar">
        <button type="button" className="btn btn--small" onClick={() => setModalOpen(true)}>
          <span aria-hidden style={{ marginRight: 6 }}>☁️</span>
          Import from OneDrive
        </button>
        {status.connected && status.account_email && (
          <span className="onedrive-bar__acct muted">{status.account_email}</span>
        )}
      </div>

      {active.length > 0 && (
        <div className="onedrive-imports">
          <div className="onedrive-imports__title">Importing from OneDrive…</div>
          {active.map((imp) => (
            <div key={imp.id} className="onedrive-imports__row">
              <span className="onedrive-imports__name" title={imp.original_name}>
                {imp.original_name}
              </span>
              <span className="onedrive-imports__state">
                {imp.status === "queued" ? "Queued" : `Downloading ${imp.progress_pct}%`}
              </span>
              <div className="progress">
                <div
                  className="progress__bar"
                  style={{ width: `${imp.status === "queued" ? 3 : imp.progress_pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <PickerModal
          caseId={caseId}
          status={status}
          onConnect={connect}
          onClose={() => setModalOpen(false)}
          onQueued={(rows) => {
            setActive((prev) => [...rows, ...prev]);
            onImportedRef.current();
            setModalOpen(false);
          }}
          onDisconnected={() => {
            void refreshStatus();
          }}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
//  Picker modal                                                               //
// --------------------------------------------------------------------------- //

function PickerModal({
  caseId,
  status,
  onConnect,
  onClose,
  onQueued,
  onDisconnected,
}: {
  caseId: number;
  status: OneDriveStatus;
  onConnect: () => void;
  onClose: () => void;
  onQueued: (rows: OneDriveImportRow[]) => void;
  onDisconnected: () => void;
}) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: "OneDrive" }]);
  const [items, setItems] = useState<OneDriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, OneDriveItem>>({});
  const [translate, setTranslate] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const current = crumbs[crumbs.length - 1];

  const load = useCallback(
    async (folderId: string | null, searchTerm: string) => {
      setLoading(true);
      setErr(null);
      try {
        const qs = new URLSearchParams();
        if (searchTerm.trim()) qs.set("search", searchTerm.trim());
        else if (folderId) qs.set("folder_id", folderId);
        const resp = await api.get<OneDriveBrowse>(
          `/integrations/onedrive/browse${qs.toString() ? `?${qs}` : ""}`,
        );
        setItems(resp.items);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : "Could not load OneDrive");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (status.connected) void load(current.id, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.connected, current.id, search]);

  function openFolder(item: OneDriveItem) {
    setSearch("");
    setSearchDraft("");
    setCrumbs((c) => [...c, { id: item.id, name: item.name }]);
  }

  function gotoCrumb(idx: number) {
    setSearch("");
    setSearchDraft("");
    setCrumbs((c) => c.slice(0, idx + 1));
  }

  function toggle(item: OneDriveItem) {
    if (!item.importable) return;
    setSelected((sel) => {
      const next = { ...sel };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });
  }

  const selectedList = Object.values(selected);

  async function submit() {
    if (selectedList.length === 0) return;
    setSubmitting(true);
    setErr(null);
    try {
      const payload: { items: OneDriveImportItemIn[]; translate: boolean } = {
        items: selectedList.map((it) => ({
          item_id: it.id,
          drive_id: it.drive_id ?? null,
          name: it.name,
          mime_type: it.mime_type ?? null,
          size_bytes: it.size_bytes ?? null,
        })),
        translate,
      };
      const resp = await api.post<OneDriveImportList>(
        `/cases/${caseId}/imports/onedrive`,
        payload,
      );
      onQueued(resp.items);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Import failed");
      setSubmitting(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect this OneDrive account? You can reconnect anytime.")) return;
    try {
      await api.del(`/integrations/onedrive`);
      onDisconnected();
      onClose();
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal__card modal__card--lg modal__card--split"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 className="modal__header-title">Import from OneDrive</h2>
            {status.connected && status.account_email && (
              <span className="muted">{status.account_email}</span>
            )}
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal__body">
          {!status.connected ? (
            <div className="onedrive-connect">
              <p>
                Connect your Microsoft account to browse your OneDrive and pull
                documents, images, and videos straight into this case. Files are
                imported in the background — you can keep working or close this
                window and check progress later.
              </p>
              <button type="button" className="btn btn--primary" onClick={onConnect}>
                Connect Microsoft account
              </button>
            </div>
          ) : (
            <>
              <form
                className="onedrive-search"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSearch(searchDraft);
                }}
              >
                <input
                  type="search"
                  placeholder="Search your OneDrive…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                />
                <button type="submit" className="btn btn--small">Search</button>
                {search && (
                  <button
                    type="button"
                    className="btn btn--small btn--ghost"
                    onClick={() => { setSearch(""); setSearchDraft(""); }}
                  >
                    Clear
                  </button>
                )}
              </form>

              {!search && (
                <div className="onedrive-crumbs">
                  {crumbs.map((c, i) => (
                    <span key={`${c.id}-${i}`}>
                      {i > 0 && <span className="onedrive-crumbs__sep">/</span>}
                      <button
                        type="button"
                        className="onedrive-crumbs__link"
                        onClick={() => gotoCrumb(i)}
                        disabled={i === crumbs.length - 1}
                      >
                        {c.name}
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {err && <div className="alert alert--error">{err}</div>}

              <div className="onedrive-list">
                {loading ? (
                  <div className="onedrive-list__empty">
                    <span className="spinner" /> Loading…
                  </div>
                ) : items.length === 0 ? (
                  <div className="onedrive-list__empty">This folder is empty.</div>
                ) : (
                  items.map((it) => {
                    const isSel = !!selected[it.id];
                    return (
                      <div
                        key={it.id}
                        className={`onedrive-row${it.is_folder ? " onedrive-row--folder" : ""}${
                          isSel ? " onedrive-row--selected" : ""
                        }${!it.is_folder && !it.importable ? " onedrive-row--disabled" : ""}`}
                        onClick={() => (it.is_folder ? openFolder(it) : toggle(it))}
                        role="button"
                        tabIndex={0}
                      >
                        {!it.is_folder && (
                          <input
                            type="checkbox"
                            checked={isSel}
                            disabled={!it.importable}
                            onChange={() => toggle(it)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <span className="onedrive-row__icon" aria-hidden>
                          {it.is_folder
                            ? "📁"
                            : it.import_kind === "video"
                            ? "🎬"
                            : "📄"}
                        </span>
                        <span className="onedrive-row__name" title={it.name}>
                          {it.name}
                        </span>
                        <span className="onedrive-row__meta muted">
                          {it.is_folder
                            ? it.child_count != null
                              ? `${it.child_count} item${it.child_count === 1 ? "" : "s"}`
                              : ""
                            : !it.importable
                            ? "Unsupported"
                            : fmtBytes(it.size_bytes)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              <label className="dropzone__option" style={{ marginTop: 12 }}>
                <input
                  type="checkbox"
                  checked={translate}
                  onChange={(e) => setTranslate(e.target.checked)}
                />
                <span>
                  Translate documents to English with AI
                  <span className="dropzone__option-sub">
                    For non-English documents. English files are left as-is.
                  </span>
                </span>
              </label>
            </>
          )}
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {status.connected && (
              <button type="button" className="btn--danger-text" onClick={disconnect}>
                Disconnect
              </button>
            )}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            {status.connected && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={selectedList.length === 0 || submitting}
                onClick={submit}
              >
                {submitting
                  ? "Starting…"
                  : `Import ${selectedList.length || ""} ${
                      selectedList.length === 1 ? "file" : "files"
                    }`.trim()}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
